import type { AdminCategory, CategoryInput, MediaAsset } from "@lucky-try/contracts";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { ImagePickerField } from "../components/media-picker";
import { Button } from "../components/ui/button";
import { ConfirmDialog, Dialog } from "../components/ui/dialog";
import { Checkbox, Field, Input, Textarea } from "../components/ui/form";
import { Badge, ErrorState, Loading, PageHeader } from "../components/ui/misc";
import { api, errorMessage } from "../lib/api";
import { formatNumber } from "../lib/format";
import { categoriesQuery } from "../lib/queries";
import { useCan } from "../lib/session";

const blank: CategoryInput = {
  slug: "",
  name: "",
  description: null,
  iconName: "ph:game-controller-duotone",
  iconAssetId: null,
  colorPrimary: null,
  colorSecondary: null,
  isActive: true,
  isFeatured: false,
  showInMenu: true,
  sortOrder: 0,
};

export function CategoriesPage() {
  const queryClient = useQueryClient();
  const canWrite = useCan("catalog.write");
  const categories = useQuery(categoriesQuery);
  const [editing, setEditing] = useState<AdminCategory | "new" | null>(null);
  const [deleting, setDeleting] = useState<AdminCategory | null>(null);

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["categories"] }),
      queryClient.invalidateQueries({ queryKey: ["games"] }),
    ]);

  const reorder = useMutation({
    mutationFn: (order: { id: number; sortOrder: number }[]) =>
      api<void>("/admin/categories/reorder", { body: { order } }),
    onSuccess: () => invalidate(),
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api<void>(`/admin/categories/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      setDeleting(null);
      await invalidate();
      toast.success("Category deleted");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  function move(index: number, direction: -1 | 1) {
    const items = categories.data?.items ?? [];
    const target = items[index + direction];
    const current = items[index];
    if (!target || !current) return;
    reorder.mutate([
      { id: current.id, sortOrder: index + direction },
      { id: target.id, sortOrder: index },
    ]);
  }

  if (categories.error)
    return <ErrorState error={categories.error} onRetry={() => void categories.refetch()} />;
  if (categories.isPending) return <Loading />;

  return (
    <>
      <PageHeader
        title="Categories"
        description="Menu order, colours and icons for the public catalog."
        actions={
          canWrite && (
            <Button variant="primary" onClick={() => setEditing("new")}>
              <PlusIcon /> New category
            </Button>
          )
        }
      />

      <div className="border-line bg-surface rounded-card overflow-hidden border">
        <table className="text-ui w-full border-collapse text-left">
          <thead className="bg-well">
            <tr className="border-line border-b">
              <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">Category</th>
              <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">Games</th>
              <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">Visibility</th>
              <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">Colours</th>
              <th className="h-9 w-28 px-3" />
            </tr>
          </thead>
          <tbody>
            {categories.data.items.map((category, index) => (
              <tr key={category.id} className="t-row border-line/60 border-b last:border-0">
                <td className="h-10 px-3">
                  <p className="text-ink font-medium">{category.name}</p>
                  <p className="text-muted text-caption">
                    /{category.slug} · {category.iconName}
                  </p>
                </td>
                <td className="tabular px-3">
                  {formatNumber(category.publishedGameCount)}
                  <span className="text-muted"> / {formatNumber(category.gameCount)}</span>
                </td>
                <td className="px-3">
                  <div className="flex items-center gap-2">
                    {!category.isActive && <Badge tone="danger">Hidden</Badge>}
                    {category.isFeatured && <Badge tone="success">Featured</Badge>}
                    <span className="text-caption text-faint">
                      {category.showInMenu ? "in menu" : "not in menu"}
                    </span>
                  </div>
                </td>
                <td className="px-3">
                  <div className="flex items-center gap-1">
                    {[category.colorPrimary, category.colorSecondary]
                      .filter(Boolean)
                      .map((color) => (
                        <span
                          key={color}
                          className="border-line size-4 rounded border"
                          style={{ background: color ?? undefined }}
                        />
                      ))}
                  </div>
                </td>
                <td className="px-3">
                  {canWrite && (
                    <div className="flex justify-end gap-0.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                        aria-label="Move up"
                      >
                        <ArrowUpIcon />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={index === categories.data.items.length - 1}
                        onClick={() => move(index, 1)}
                        aria-label="Move down"
                      >
                        <ArrowDownIcon />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(category)}
                        aria-label="Edit"
                      >
                        <PencilSimpleIcon />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleting(category)}
                        aria-label="Delete"
                      >
                        <TrashIcon />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <CategoryDialog
          category={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await invalidate();
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete category"
        message={
          deleting && deleting.gameCount > 0
            ? `${deleting.name} still holds ${deleting.gameCount} games; move them first.`
            : `Delete ${deleting?.name}?`
        }
        confirmLabel="Delete"
        danger
        pending={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        onClose={() => setDeleting(null)}
      />
    </>
  );
}

function CategoryDialog({
  category,
  onClose,
  onSaved,
}: {
  category: AdminCategory | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [values, setValues] = useState<CategoryInput>(
    category
      ? {
          slug: category.slug,
          name: category.name,
          description: category.description,
          iconName: category.iconName,
          iconAssetId: category.iconAssetId,
          colorPrimary: category.colorPrimary,
          colorSecondary: category.colorSecondary,
          isActive: category.isActive,
          isFeatured: category.isFeatured,
          showInMenu: category.showInMenu,
          sortOrder: category.sortOrder,
        }
      : blank,
  );
  const [iconAsset, setIconAsset] = useState<MediaAsset | null>(null);

  const save = useMutation({
    mutationFn: async (body: CategoryInput): Promise<void> => {
      if (category) await api<void>(`/admin/categories/${category.id}`, { method: "PUT", body });
      else await api<{ id: number }>("/admin/categories", { body });
    },
    onSuccess: () => {
      toast.success("Category saved");
      onSaved();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const patch = (next: Partial<CategoryInput>) => setValues((current) => ({ ...current, ...next }));
  const text = (value: string) => (value.trim() === "" ? null : value.trim());

  return (
    <Dialog
      open
      onClose={onClose}
      width="lg"
      title={category ? `Edit ${category.name}` : "New category"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate(values)}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Name">
          <Input value={values.name} onChange={(event) => patch({ name: event.target.value })} />
        </Field>
        <Field label="Slug">
          <Input value={values.slug} onChange={(event) => patch({ slug: event.target.value })} />
        </Field>
        <Field
          label="Icon name"
          hint="Phosphor icon, e.g. ph:sword-duotone."
          className="md:col-span-2"
        >
          <Input
            value={values.iconName}
            onChange={(event) => patch({ iconName: event.target.value })}
          />
        </Field>
        <Field
          label="Description"
          hint="Shown above the games on the category page. A blank line starts a new paragraph."
          className="md:col-span-2"
        >
          <Textarea
            rows={6}
            value={values.description ?? ""}
            onChange={(event) => patch({ description: text(event.target.value) })}
          />
        </Field>
        <Field label="Primary colour" hint="#RRGGBB">
          <Input
            placeholder="#7c5cff"
            value={values.colorPrimary ?? ""}
            onChange={(event) => patch({ colorPrimary: text(event.target.value) })}
          />
        </Field>
        <Field label="Secondary colour" hint="#RRGGBB">
          <Input
            placeholder="#22d3ee"
            value={values.colorSecondary ?? ""}
            onChange={(event) => patch({ colorSecondary: text(event.target.value) })}
          />
        </Field>
        <Field label="Sort order">
          <Input
            type="number"
            min={0}
            value={values.sortOrder}
            onChange={(event) => patch({ sortOrder: Number(event.target.value) })}
          />
        </Field>
        <ImagePickerField
          label="Custom artwork"
          hint="Optional; overrides the icon on category cards."
          asset={iconAsset}
          onChange={(asset) => {
            setIconAsset(asset);
            patch({ iconAssetId: asset?.id ?? null });
          }}
        />
        <div className="flex flex-col gap-2 md:col-span-2">
          <Checkbox
            label="Active"
            checked={values.isActive}
            onChange={(event) => patch({ isActive: event.target.checked })}
          />
          <Checkbox
            label="Show in menu"
            checked={values.showInMenu}
            onChange={(event) => patch({ showInMenu: event.target.checked })}
          />
          <Checkbox
            label="Featured on the home page"
            checked={values.isFeatured}
            onChange={(event) => patch({ isFeatured: event.target.checked })}
          />
        </div>
      </div>
    </Dialog>
  );
}
